import { useCallback, useEffect } from 'react';

import type { Event, RiveEventPayload } from '@rive-app/react-canvas';
import { EventType, Fit, Layout } from '@rive-app/react-canvas';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import signUpEmailRiv from '../../../../assets/auth/sign_up_email_v3.riv';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { analytics } from '../../../../services/analytics';

import styles from './EmailVerificationSuccessRedesign.module.scss';

const STATE_MACHINE_NAME = 'State Machine 1';
const ARTBOARD_NAME = 'Email';

export const EmailVerificationSuccessRedesign = observer(function EmailVerificationSuccessRedesign(
  props: WizardChildProps,
) {
  const { t } = useTranslation();

  const onClose = useCallback(() => {
    if (props.isCurrent) {
      void analytics.sendEvent('email_verified_success', {
        event_category: 'sign_up',
        event_action: 'click',
        button_text: 'close',
      });
      props.nextStep?.();
    }
  }, [props]);

  const onRiveEvent = useCallback(
    (event: Event) => {
      const data = (event?.data || {}) as RiveEventPayload;
      if (data.name && (data.name === 'finish' || data.name === 'close_animation_completed')) {
        onClose();
      }
    },
    [onClose],
  );

  const { rive, RiveComponent } = useRiveMachine({
    src: signUpEmailRiv,
    artboard: ARTBOARD_NAME,
    stateMachineName: STATE_MACHINE_NAME,
    autoplay: false,
    layout: new Layout({
      fit: Fit.Contain,
    }),
  });

  useEffect(() => {
    if (rive && props.isCurrent) {
      rive.off(EventType.RiveEvent, onRiveEvent);

      rive.on(EventType.RiveEvent, onRiveEvent);
      rive.setTextRunValue('success', t('dialogs.successLowerCase'));
      rive.setTextRunValue('yourEmail', t('auth.emailVerifiedSuccessfully'));
      rive.setTextRunValue('close', t('common.close'));
      rive.play();

      return () => {
        rive.off(EventType.RiveEvent, onRiveEvent);
      };
    }
  }, [rive, onRiveEvent, props.isCurrent, t]);

  return (
    <div className={styles.container}>
      <RiveComponent className={styles.rive} />
    </div>
  );
});
